// Scrollspy for the docs TOC (ggcoin.ai#273). The active section is computed
// from live header positions on scroll (rAF-throttled, passive); the decision
// itself is a pure helper so it unit-tests without a DOM.

import { createSignal, onCleanup, onMount } from "solid-js";

/** How far below the viewport top the "reading line" sits (past the sticky nav). */
const READING_LINE_PX = 120;

/**
 * Pick the active section: the LAST one whose top edge has crossed above the
 * reading line — i.e. the section the reader is currently inside. Before the
 * first section, nothing is active.
 */
export function activeSection(
  tops: readonly { id: string; top: number }[],
  line: number,
): string | undefined {
  let active: string | undefined;
  for (const t of tops) {
    if (t.top <= line) active = t.id;
  }
  return active;
}

/** Reactive scrollspy over the given section ids. Returns the active-id accessor. */
export function useScrollSpy(ids: readonly string[]): () => string | undefined {
  const [active, setActive] = createSignal<string | undefined>(undefined);
  onMount(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const tops = ids.flatMap((id) => {
        const el = document.getElementById(id);
        return el ? [{ id, top: el.getBoundingClientRect().top }] : [];
      });
      setActive(activeSection(tops, READING_LINE_PX));
    };
    const onScroll = () => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };
    measure();
    // Scroll moves the reading line; resize/orientation (and the reflows they
    // trigger) move the section tops without any scroll event.
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    onCleanup(() => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== 0) cancelAnimationFrame(frame);
    });
  });
  return active;
}
